import React from 'react'
import { connect } from 'react-redux'
import styled from '@emotion/styled'
import { uniqBy } from 'lodash-es'
import { HolidayDetail, CrudActions, CrudEventEnum, Holiday, UserPreference, Currency } from 'api'
import { ModalWindow, Groupbox, ModalFooter } from 'view/components/panels'
import { DialogRow, DialogField } from 'view/components/inputs'
import { CurrencySelectboxField, TradingDatePickerField } from 'view/shared/entity'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { ModalConsumerProps, withModal } from 'view/shared/modal'
import { newEmptyHolidayDetailForAdd } from 'model/adminUtils'
import { notEmpty, getDateString } from 'utils'
import { Translation, Locale } from 'locale'
import { createStructuredSelector } from 'reselect'
import { createHolidayGetterSelector } from 'selectors/holidaySelectors'
import { getDateEditorFormat } from 'model'
import { createUserPreferenceSelector } from 'selectors/userPreferenceSelectors'

export interface HolidayDialogProps extends ModalConsumerProps {
  getHoliday: (currency: string) => Holiday | null
  onUpsert: (holiday: Holiday) => unknown
  onClose?: () => unknown
  userPreference: UserPreference
}

interface HolidayDialogState {
  holidayDetail: HolidayDetail
}

const mapStateToProps = createStructuredSelector({
  getHoliday: createHolidayGetterSelector,
  userPreference: createUserPreferenceSelector()
})

const mapDispatchToProps = {
  onUpsert: CrudActions.holidayUpsert
}

const Content = styled(Groupbox)`
  padding: 20px 20px 0 20px;

  & > *:last-child {
    border-bottom: none;
  }
`

export class HolidayDialogComponent extends React.Component<HolidayDialogProps> {
  state: HolidayDialogState

  constructor(props: HolidayDialogProps) {
    super(props)
    this.state = { holidayDetail: newEmptyHolidayDetailForAdd() }
  }

  render() {
    const { holiday, date, availableEvents } = this.state.holidayDetail
    const title = Locale.translate(Translation.TC_NEW_HOLIDAY)
    const readOnly = !availableEvents.includes(CrudEventEnum.UPSERT)

    return (
      <ModalWindow title={title}>
        <Content>
          <DialogRow>
            <DialogField>
              <CurrencySelectboxField
                dataTestId={'HolidayDialog-TextInputField-HolidayCurrency'}
                label={Locale.translate(Translation.SC_HOLIDAY_CURRENCY)}
                onUpdate={this.handleCurrencyUpdate}
                readOnly={readOnly}
                value={holiday.currency as Currency}
              />
            </DialogField>
            <DialogField>
              <TradingDatePickerField
                dataTestId={'HolidayDialog-TradingDatePickerField-Holiday'}
                currency={holiday.currency}
                label={Locale.translate(Translation.SC_HOLIDAY)}
                onUpdate={this.handleDateUpdate}
                readOnly={readOnly}
                value={date}
                dateFormat={getDateEditorFormat(this.props.userPreference.dateFormat)}
                dispatchNullWhenInvalid={true}
                validateDate={true}
              />
            </DialogField>
          </DialogRow>
        </Content>
        <ModalFooter>
          <DialogRow>
            <DialogField>
              <TextButton dataTestId={'HolidayDialog-TextButton-ButtonCancel'} type={TextButtonType.Tertiary} onClick={this.props.onClose}>
                {Locale.translate(Translation.TC_BUTTON_CANCEL)}
              </TextButton>
            </DialogField>
            <DialogField flex={0}>
              <TextButton
                dataTestId={'HolidayDialog-TextButton-ButtonSave'}
                type={TextButtonType.Primary}
                disabled={!this.canSave()}
                onClick={this.onSave}>
                {Locale.translate(Translation.TC_BUTTON_SAVE)}
              </TextButton>
            </DialogField>
          </DialogRow>
        </ModalFooter>
      </ModalWindow>
    )
  }

  private handleCurrencyUpdate = (value: Currency) => {
    const holiday = this.props.getHoliday(value.toUpperCase())
    const holidayDetail: HolidayDetail = {
      ...this.state.holidayDetail,
      holiday: holiday || {
        ...this.state.holidayDetail.holiday,
        holidays: [],
        currency: value
      }
    }

    this.setState({ holidayDetail })
  }

  private handleDateUpdate = (value: string) => {
    const holidayDetail: HolidayDetail = {
      ...this.state.holidayDetail,
      date: value
    }

    this.setState({ holidayDetail })
  }

  private canSave = () => {
    const { holiday, date, availableEvents } = this.state.holidayDetail
    const isUpsertAvailable = availableEvents.includes(CrudEventEnum.UPSERT)

    return isUpsertAvailable && notEmpty(holiday.currency) && (typeof date === 'string' ? notEmpty(date) : !!date)
  }

  private onSave = () => {
    const { holiday, date: newDate } = this.state.holidayDetail
    const newHoliday: Holiday = {
      ...holiday,
      holidays: uniqBy([...holiday.holidays, newDate], getDateString)
    }

    this.props.onUpsert(newHoliday)

    if (this.props.onClose) {
      this.props.onClose()
    }
  }
}

export const HolidayDialog = withModal(connect(mapStateToProps, mapDispatchToProps)(HolidayDialogComponent))
